import { Helmet } from "react-helmet-async";
import React, { useContext, useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { CartContext } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import API_URL from "../config";

export default function ProductDetail() {
  const { id } = useParams();
  const { addToCart } = useContext(CartContext);
  const { format } = useCurrency();                          // ← add this

  const [product, setProduct]             = useState(null);
  const [loading, setLoading]             = useState(true);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize,  setSelectedSize]  = useState(null);
  const [quantity, setQuantity]           = useState(1);
  const [added,    setAdded]              = useState(false);
  const [sizeError, setSizeError]         = useState(false);
  const [activeImg, setActiveImg]         = useState(0);

  const [reviews, setReviews]               = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [hoveredStar, setHoveredStar]       = useState(0);
  const [submitStatus, setSubmitStatus]     = useState("idle");
  const [reviewForm, setReviewForm]         = useState({ name: "", rating: 0, comment: "" });
  const [formError, setFormError]           = useState("");

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res  = await fetch(`${API_URL}/api/products/${id}`);
        const data = await res.json();
        setProduct(data);
      } catch (err) {
        console.error("Failed to fetch product:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res  = await fetch(`${API_URL}/api/reviews/${id}`);
        const data = await res.json();
        setReviews(data);
      } catch (err) {
        console.error("Failed to fetch reviews:", err);
      } finally {
        setReviewsLoading(false);
      }
    };
    fetchReviews();
  }, [id]);

  useEffect(() => {
    if (product) {
      const hasColors = product.colors && product.colors.length > 0;
      setSelectedColor(hasColors ? product.colors[0] : null);
      setSelectedSize(null);
      setQuantity(1);
      setActiveImg(0);
    }
  }, [product]);

  const handleAddToCart = () => {
    if (!product) return;
    const hasSizes = product.sizes && product.sizes.length > 0;
    if (hasSizes && !selectedSize) {
      setSizeError(true);
      setTimeout(() => setSizeError(false), 2000);
      return;
    }
    for (let i = 0; i < quantity; i++) {
      addToCart(product, selectedSize, selectedColor?.name || null);
    }
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!reviewForm.name.trim())    return setFormError("Please enter your name.");
    if (reviewForm.rating === 0)    return setFormError("Please select a star rating.");
    if (!reviewForm.comment.trim()) return setFormError("Please write a comment.");
    setSubmitStatus("submitting");
    try {
      const res = await fetch(`${API_URL}/api/reviews/${id}`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(reviewForm)
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Submit failed");
      }
      const newReview = await res.json();
      setReviews(prev => [newReview, ...prev]);
      setReviewForm({ name: "", rating: 0, comment: "" });
      setHoveredStar(0);
      setSubmitStatus("success");
      setTimeout(() => setSubmitStatus("idle"), 3000);
    } catch (err) {
      setSubmitStatus("error");
      setFormError(err.message || "Something went wrong. Try again.");
      setTimeout(() => setSubmitStatus("idle"), 3000);
    }
  };

  const avgRating = reviews.length
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  const ratingCounts = [5, 4, 3, 2, 1].map(star => ({
    star,
    count: reviews.filter(r => r.rating === star).length,
    pct: reviews.length
      ? Math.round((reviews.filter(r => r.rating === star).length / reviews.length) * 100)
      : 0
  }));

  const formatDate = (iso) => new Date(iso).toLocaleDateString("en-AE", {
    year: "numeric", month: "short", day: "numeric"
  });

  if (loading) {
    return (
      <><style>{pdStyles}</style>
      <div className="pd-root">
        <div className="pd-loading"><div className="pd-spinner" /><p>Loading product…</p></div>
      </div></>
    );
  }

  if (!product) {
    return (
      <><style>{pdStyles}</style>
      <div className="pd-root">
        <div className="pd-not-found">
          <p className="pd-nf-code">404</p>
          <h2 className="pd-nf-title">Product not found</h2>
          <p className="pd-nf-sub">It may have been removed or the link is incorrect.</p>
          <Link to="/products" className="pd-back-btn">← Back to Shop</Link>
        </div>
      </div></>
    );
  }

  const hasColors = product.colors && product.colors.length > 0;
  const hasSizes  = product.sizes  && product.sizes.length  > 0;
  const images    = product.images?.length ? product.images : [product.image];

  return (
    <><style>{pdStyles}</style>

    <Helmet>
      <title>{product ? `${product.name} | STMZ Group Dubai` : "Loading Product... | STMZ Group"}</title>
      <meta
        name="description"
        content={product?.description ? product.description.substring(0, 150) : "Discover premium fashion and lifestyle products at STMZ Group Dubai."}
      />
      <meta property="og:title"       content={product ? `${product.name} - STMZ Group` : "STMZ Group Premium Collection"} />
      <meta property="og:image"       content={images[activeImg] || images[0]} />
      <meta property="og:description" content={`Shop ${product?.name || "our latest arrivals"} online. Premium quality guaranteed.`} />
      <meta property="og:url"         content={`https://www.stmzgroup.com/product/${id}`} />
    </Helmet>

    <div className="pd-root">

      <nav className="pd-breadcrumb">
        <Link to="/">Home</Link><span>/</span>
        <Link to="/products">Shop</Link><span>/</span>
        <span>{product.name}</span>
      </nav>

      <div className="pd-layout">

        {/* ── Images ── */}
        <div className="pd-images">
          <div className="pd-img-main">
            <img
              src={images[activeImg]}
              alt={`${product.name} - STMZ Group Premium Collection`}
              className="pd-main-img"
              loading="eager"
            />
            {product.isNewArrival && <span className="pd-new-badge">New Arrival</span>}
          </div>
          {images.length > 1 && (
            <div className="pd-thumbs">
              {images.map((img, i) => (
                <button key={i} className={`pd-thumb ${activeImg === i ? "active" : ""}`} onClick={() => setActiveImg(i)}>
                  <img src={img} alt="" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Info ── */}
        <div className="pd-info">
          {product.category && <p className="pd-category">{product.category}</p>}
          <h1 className="pd-name">{product.name}</h1>

          {avgRating && (
            <div className="pd-rating-summary">
              <span className="pd-rating-stars-sm">
                {[1,2,3,4,5].map(s => (
                  <span key={s} className={s <= Math.round(avgRating) ? "pd-star-filled" : "pd-star-empty"}>★</span>
                ))}
              </span>
              <span className="pd-rating-avg-sm">{avgRating}</span>
              <a href="#reviews" className="pd-rating-count-link">
                ({reviews.length} review{reviews.length !== 1 ? "s" : ""})
              </a>
            </div>
          )}

          {/*
            ✅ FIX: was `{product.price} <span>PKR</span>`
            Now uses format() which auto-converts PKR → visitor's local currency
          */}
          <p className="pd-price">{format(product.price)}</p>

          <div className="pd-divider" />

          {hasColors && (
            <div className="pd-section">
              <p className="pd-section-label">
                Colour — <span className="pd-selected-val">{selectedColor?.name}</span>
              </p>
              <div className="pd-color-row">
                {product.colors.map((color) => (
                  <button
                    key={color.name}
                    title={color.name}
                    className={`pd-color-btn ${selectedColor?.name === color.name ? "active" : ""}`}
                    onClick={() => setSelectedColor(color)}
                  >
                    <span className="pd-color-inner" style={{ background: color.hex }} />
                  </button>
                ))}
              </div>
            </div>
          )}

          {hasSizes && (
            <div className="pd-section">
              <div className="pd-size-header">
                <p className="pd-section-label">
                  Size — <span className="pd-selected-val">{selectedSize || "Not selected"}</span>
                </p>
                <button className="pd-size-guide">Size Guide</button>
              </div>
              <div className="pd-size-row">
                {product.sizes.map((size) => (
                  <button
                    key={size}
                    className={`pd-size-btn ${selectedSize === size ? "active" : ""}`}
                    onClick={() => { setSelectedSize(size); setSizeError(false); }}
                  >
                    {size}
                  </button>
                ))}
              </div>
              {sizeError && <p className="pd-size-error">Please select a size to continue</p>}
            </div>
          )}

          <div className="pd-section">
            <p className="pd-section-label">Quantity</p>
            <div className="pd-qty-row">
              <button className="pd-qty-btn" onClick={() => setQuantity(q => Math.max(1, q - 1))}>−</button>
              <span className="pd-qty-num">{quantity}</span>
              <button className="pd-qty-btn" onClick={() => setQuantity(q => q + 1)}>+</button>
            </div>
          </div>

          <div className="pd-divider" />

          <div className="pd-actions">
            <button
              onClick={handleAddToCart}
              className={`pd-add-btn ${added ? "pd-added" : sizeError ? "pd-error" : ""}`}
            >
              {added ? "✓ Added to Cart" : sizeError ? "Select a Size First" : "Add to Cart"}
            </button>
            <Link to="/cart" className="pd-view-cart-btn">View Cart</Link>
          </div>

          {product.description && (
            <div className="pd-desc-section">
              <p className="pd-section-label" style={{ marginBottom: 10 }}>Description</p>
              <p className="pd-desc">{product.description}</p>
            </div>
          )}
        </div>
      </div>

      {/* ══ REVIEWS ══ */}
      <div className="pd-reviews-root" id="reviews">

        <div className="pd-reviews-header">
          <div>
            <p className="pd-reviews-eyebrow">Customer Reviews</p>
            <h2 className="pd-reviews-title">
              {reviews.length === 0 ? "No reviews yet" : `${reviews.length} Review${reviews.length !== 1 ? "s" : ""}`}
            </h2>
          </div>
          {avgRating && (
            <div className="pd-reviews-score">
              <span className="pd-score-num">{avgRating}</span>
              <div className="pd-score-stars">
                {[1,2,3,4,5].map(s => (
                  <span key={s} className={s <= Math.round(avgRating) ? "pd-star-filled" : "pd-star-empty"}>★</span>
                ))}
              </div>
              <span className="pd-score-label">out of 5</span>
            </div>
          )}
        </div>

        {reviews.length > 0 && (
          <div className="pd-rating-bars">
            {ratingCounts.map(({ star, count, pct }) => (
              <div key={star} className="pd-rating-bar-row">
                <span className="pd-bar-label">{star} ★</span>
                <div className="pd-bar-track">
                  <div className="pd-bar-fill" style={{ width: `${pct}%` }} />
                </div>
                <span className="pd-bar-count">{count}</span>
              </div>
            ))}
          </div>
        )}

        <div className="pd-reviews-body">

          {/* Review Form */}
          <div className="pd-review-form-wrap">
            <h3 className="pd-form-title">Write a Review</h3>
            <form onSubmit={handleReviewSubmit} className="pd-review-form">

              <div className="pd-rf-field">
                <label className="pd-rf-label">Your Name</label>
                <input
                  className="pd-rf-input"
                  placeholder="e.g. Fatima A."
                  value={reviewForm.name}
                  onChange={e => setReviewForm(f => ({ ...f, name: e.target.value }))}
                />
              </div>

              <div className="pd-rf-field">
                <label className="pd-rf-label">Rating</label>
                <div className="pd-star-picker">
                  {[1,2,3,4,5].map(star => (
                    <button
                      key={star}
                      type="button"
                      className={`pd-star-pick-btn ${star <= (hoveredStar || reviewForm.rating) ? "active" : ""}`}
                      onMouseEnter={() => setHoveredStar(star)}
                      onMouseLeave={() => setHoveredStar(0)}
                      onClick={() => setReviewForm(f => ({ ...f, rating: star }))}
                    >★</button>
                  ))}
                  {reviewForm.rating > 0 && (
                    <span className="pd-star-label">
                      {["","Poor","Fair","Good","Very Good","Excellent"][reviewForm.rating]}
                    </span>
                  )}
                </div>
              </div>

              <div className="pd-rf-field">
                <label className="pd-rf-label">Your Review</label>
                <textarea
                  className="pd-rf-input pd-rf-textarea"
                  rows={4}
                  placeholder="Share your thoughts about this product…"
                  value={reviewForm.comment}
                  onChange={e => setReviewForm(f => ({ ...f, comment: e.target.value }))}
                />
              </div>

              {formError && <p className="pd-rf-error">{formError}</p>}

              <button
                type="submit"
                disabled={submitStatus === "submitting"}
                className={`pd-rf-submit ${submitStatus === "success" ? "pd-rf-success" : ""}`}
              >
                {submitStatus === "submitting" ? "Submitting…"
                  : submitStatus === "success"  ? "✓ Review Submitted!"
                  : "Submit Review"}
              </button>

            </form>
          </div>

          {/* Review List */}
          <div className="pd-reviews-list">
            {reviewsLoading ? (
              <div className="pd-reviews-loading"><div className="pd-spinner" /></div>
            ) : reviews.length === 0 ? (
              <div className="pd-no-reviews">
                <p className="pd-no-reviews-icon">✦</p>
                <p className="pd-no-reviews-text">Be the first to review this product</p>
              </div>
            ) : (
              reviews.map(review => (
                <div key={review._id} className="pd-review-card">
                  <div className="pd-review-top">
                    <div className="pd-review-avatar">{review.name.charAt(0).toUpperCase()}</div>
                    <div className="pd-review-meta">
                      <p className="pd-review-name">{review.name}</p>
                      <p className="pd-review-date">{formatDate(review.createdAt)}</p>
                    </div>
                    <div className="pd-review-stars">
                      {[1,2,3,4,5].map(s => (
                        <span key={s} className={s <= review.rating ? "pd-star-filled" : "pd-star-empty"}>★</span>
                      ))}
                    </div>
                  </div>
                  <p className="pd-review-comment">{review.comment}</p>
                </div>
              ))
            )}
          </div>

        </div>
      </div>

    </div></>
  );
}

const pdStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap');
  .pd-root{font-family:'DM Sans',sans-serif;background:#fdf9f6;min-height:80vh;padding:32px 40px 80px;max-width:1160px;margin:0 auto}
  .pd-breadcrumb{display:flex;align-items:center;gap:8px;font-size:12px;color:#a09890;margin-bottom:36px;letter-spacing:.02em}
  .pd-breadcrumb a{color:#a09890;text-decoration:none;transition:color .18s}
  .pd-breadcrumb a:hover{color:#c87090}
  .pd-breadcrumb span:last-child{color:#1a1410}
  .pd-layout{display:grid;grid-template-columns:1fr 1fr;gap:64px;align-items:start}
  @media(max-width:768px){.pd-root{padding:20px 20px 60px}.pd-layout{grid-template-columns:1fr;gap:32px}}
  .pd-img-main{position:relative;background:#f0ebe5;overflow:hidden}
  .pd-main-img{width:100%;aspect-ratio:3/4;object-fit:cover;display:block;transition:transform .55s ease}
  .pd-img-main:hover .pd-main-img{transform:scale(1.03)}
  .pd-new-badge{position:absolute;top:16px;left:16px;background:#1a1410;color:#fdf9f6;font-size:9px;font-weight:500;letter-spacing:.2em;text-transform:uppercase;padding:5px 11px}
  .pd-thumbs{display:flex;gap:10px;margin-top:10px}
  .pd-thumb{width:72px;height:90px;border:2px solid transparent;cursor:pointer;padding:0;overflow:hidden;background:#f0ebe5;transition:border-color .18s}
  .pd-thumb.active{border-color:#1a1410}
  .pd-thumb img{width:100%;height:100%;object-fit:cover;display:block}
  .pd-category{font-size:10px;font-weight:500;letter-spacing:.26em;text-transform:uppercase;color:#c87090;margin-bottom:12px}
  .pd-name{font-family:'Cormorant Garamond',serif;font-size:clamp(32px,4vw,48px);font-weight:300;color:#1a1410;line-height:1.12;margin:0 0 10px;letter-spacing:-.01em}
  .pd-rating-summary{display:flex;align-items:center;gap:8px;margin-bottom:14px}
  .pd-rating-stars-sm{display:flex;gap:1px}
  .pd-star-filled{color:#c87090;font-size:14px}
  .pd-star-empty{color:#ddd8d2;font-size:14px}
  .pd-rating-avg-sm{font-size:13px;font-weight:500;color:#1a1410}
  .pd-rating-count-link{font-size:12px;color:#a09890;text-decoration:none;border-bottom:1px solid #ddd8d2;transition:color .18s}
  .pd-rating-count-link:hover{color:#c87090}

  /* ✅ Price now shows local currency — no more hardcoded PKR label */
  .pd-price{font-size:22px;font-weight:500;color:#c87090;letter-spacing:.04em}

  .pd-divider{height:1px;background:#ede8e2;margin:24px 0}
  .pd-section{margin-bottom:22px}
  .pd-section-label{font-size:11px;font-weight:500;letter-spacing:.18em;text-transform:uppercase;color:#7a6e68;margin-bottom:12px}
  .pd-selected-val{font-family:'Cormorant Garamond',serif;font-size:14px;font-style:italic;font-weight:400;text-transform:none;letter-spacing:0;color:#1a1410}
  .pd-color-row{display:flex;gap:10px;flex-wrap:wrap}
  .pd-color-btn{width:34px;height:34px;border-radius:50%;border:3px solid transparent;padding:2px;cursor:pointer;background:transparent;transition:border-color .18s,transform .18s}
  .pd-color-btn:hover{transform:scale(1.12)}
  .pd-color-btn.active{border-color:#1a1410}
  .pd-color-inner{width:100%;height:100%;border-radius:50%;display:block;box-shadow:inset 0 0 0 1px rgba(0,0,0,.08)}
  .pd-size-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px}
  .pd-size-header .pd-section-label{margin-bottom:0}
  .pd-size-guide{font-size:11px;color:#a09890;background:none;border:none;cursor:pointer;text-decoration:underline;font-family:'DM Sans',sans-serif;padding:0;transition:color .18s}
  .pd-size-guide:hover{color:#c87090}
  .pd-size-row{display:flex;gap:8px;flex-wrap:wrap}
  .pd-size-btn{min-width:48px;padding:8px 14px;border:1.5px solid #ddd8d2;background:transparent;font-size:12px;font-weight:500;font-family:'DM Sans',sans-serif;letter-spacing:.08em;color:#7a6e68;cursor:pointer;transition:all .18s}
  .pd-size-btn:hover{border-color:#1a1410;color:#1a1410}
  .pd-size-btn.active{background:#1a1410;color:#fdf9f6;border-color:#1a1410}
  .pd-size-error{font-size:12px;color:#c87090;font-style:italic;margin-top:8px;animation:pd-shake .3s ease}
  .pd-qty-row{display:flex;align-items:center;width:fit-content;border:1.5px solid #ddd8d2}
  .pd-qty-btn{width:40px;height:40px;background:transparent;border:none;font-size:18px;color:#1a1410;cursor:pointer;font-family:'DM Sans',sans-serif;transition:background .15s;display:flex;align-items:center;justify-content:center}
  .pd-qty-btn:hover{background:#f0ebe5}
  .pd-qty-num{min-width:44px;text-align:center;font-size:15px;font-weight:500;color:#1a1410;border-left:1.5px solid #ddd8d2;border-right:1.5px solid #ddd8d2;height:40px;line-height:40px}
  .pd-actions{display:flex;gap:12px;margin-bottom:28px;flex-wrap:wrap}
  .pd-add-btn{flex:1;min-width:180px;padding:15px 24px;font-family:'DM Sans',sans-serif;font-size:11px;font-weight:500;letter-spacing:.2em;text-transform:uppercase;border:none;cursor:pointer;transition:background .22s;background:#1a1410;color:#fdf9f6}
  .pd-add-btn:hover{background:#c87090}
  .pd-add-btn.pd-added{background:#c87090}
  .pd-add-btn.pd-error{background:#f5e6ea;color:#c87090}
  .pd-view-cart-btn{padding:15px 22px;font-size:11px;font-weight:500;letter-spacing:.2em;text-transform:uppercase;border:1.5px solid #1a1410;color:#1a1410;text-decoration:none;transition:all .22s;white-space:nowrap;display:flex;align-items:center}
  .pd-view-cart-btn:hover{background:#1a1410;color:#fdf9f6}
  .pd-desc-section{background:#f7f3ef;padding:20px}
  .pd-desc{font-size:14px;font-weight:300;color:#5a524c;line-height:1.8}
  .pd-reviews-root{margin-top:72px;border-top:1px solid #ede8e2;padding-top:56px}
  .pd-reviews-header{display:flex;align-items:flex-start;justify-content:space-between;flex-wrap:wrap;gap:20px;margin-bottom:32px}
  .pd-reviews-eyebrow{font-size:10px;font-weight:500;letter-spacing:.26em;text-transform:uppercase;color:#c87090;margin-bottom:8px}
  .pd-reviews-title{font-family:'Cormorant Garamond',serif;font-size:32px;font-weight:300;color:#1a1410;margin:0}
  .pd-reviews-score{text-align:center}
  .pd-score-num{font-family:'Cormorant Garamond',serif;font-size:52px;font-weight:300;color:#1a1410;line-height:1;display:block}
  .pd-score-stars{display:flex;gap:3px;justify-content:center;margin:6px 0 4px}
  .pd-score-label{font-size:11px;color:#a09890;letter-spacing:.1em;text-transform:uppercase}
  .pd-rating-bars{max-width:340px;margin-bottom:44px;display:flex;flex-direction:column;gap:8px}
  .pd-rating-bar-row{display:flex;align-items:center;gap:10px}
  .pd-bar-label{font-size:12px;color:#7a6e68;width:30px;flex-shrink:0}
  .pd-bar-track{flex:1;height:6px;background:#f0ebe5;border-radius:3px;overflow:hidden}
  .pd-bar-fill{height:100%;background:#c87090;border-radius:3px;transition:width .4s ease}
  .pd-bar-count{font-size:11px;color:#a09890;width:18px;text-align:right;flex-shrink:0}
  .pd-reviews-body{display:grid;grid-template-columns:360px 1fr;gap:48px;align-items:start}
  @media(max-width:860px){.pd-reviews-body{grid-template-columns:1fr;gap:36px}}
  .pd-review-form-wrap{background:#fff;border:1px solid #ede8e2;padding:28px}
  .pd-form-title{font-family:'Cormorant Garamond',serif;font-size:22px;font-weight:400;color:#1a1410;margin:0 0 22px}
  .pd-review-form{display:flex;flex-direction:column;gap:18px}
  .pd-rf-field{display:flex;flex-direction:column;gap:7px}
  .pd-rf-label{font-size:10.5px;font-weight:500;letter-spacing:.18em;text-transform:uppercase;color:#7a6e68}
  .pd-rf-input{border:1.5px solid #ddd8d2;background:#fdf9f6;padding:10px 12px;font-size:13.5px;font-family:'DM Sans',sans-serif;color:#1a1410;outline:none;transition:border-color .18s;width:100%;box-sizing:border-box}
  .pd-rf-input:focus{border-color:#c87090}
  .pd-rf-textarea{resize:vertical;min-height:100px}
  .pd-star-picker{display:flex;align-items:center;gap:4px}
  .pd-star-pick-btn{background:none;border:none;font-size:28px;cursor:pointer;color:#ddd8d2;transition:color .12s,transform .12s;padding:0;line-height:1}
  .pd-star-pick-btn.active{color:#c87090}
  .pd-star-pick-btn:hover{transform:scale(1.15)}
  .pd-star-label{font-size:12px;font-style:italic;color:#7a6e68;margin-left:6px;font-family:'Cormorant Garamond',serif}
  .pd-rf-error{font-size:12px;color:#c87090;font-style:italic}
  .pd-rf-submit{padding:13px;background:#1a1410;color:#fdf9f6;border:none;font-size:11px;font-weight:500;font-family:'DM Sans',sans-serif;letter-spacing:.18em;text-transform:uppercase;cursor:pointer;transition:background .22s}
  .pd-rf-submit:hover{background:#c87090}
  .pd-rf-submit:disabled{opacity:.55;cursor:not-allowed}
  .pd-rf-success{background:#c87090 !important}
  .pd-reviews-list{display:flex;flex-direction:column;gap:20px}
  .pd-reviews-loading{display:flex;justify-content:center;padding:40px 0}
  .pd-no-reviews{text-align:center;padding:48px 20px}
  .pd-no-reviews-icon{font-size:28px;color:#ddd8d2;margin-bottom:12px}
  .pd-no-reviews-text{font-family:'Cormorant Garamond',serif;font-size:18px;font-style:italic;font-weight:300;color:#a09890}
  .pd-review-card{background:#fff;border:1px solid #ede8e2;padding:22px}
  .pd-review-top{display:flex;align-items:center;gap:12px;margin-bottom:14px}
  .pd-review-avatar{width:38px;height:38px;border-radius:50%;background:#f0ebe5;display:flex;align-items:center;justify-content:center;font-family:'Cormorant Garamond',serif;font-size:16px;color:#c87090;font-weight:400;flex-shrink:0}
  .pd-review-meta{flex:1}
  .pd-review-name{font-size:14px;font-weight:500;color:#1a1410;margin:0 0 2px}
  .pd-review-date{font-size:11px;color:#a09890;margin:0}
  .pd-review-stars{display:flex;gap:2px}
  .pd-review-comment{font-size:14px;font-weight:300;color:#5a524c;line-height:1.75;margin:0}
  .pd-loading{display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:50vh;gap:18px;color:#a09890;font-size:14px}
  .pd-spinner{width:36px;height:36px;border:2px solid #ede8e2;border-top-color:#c87090;border-radius:50%;animation:pd-spin .8s linear infinite}
  .pd-not-found{display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:50vh;gap:10px;text-align:center}
  .pd-nf-code{font-family:'Cormorant Garamond',serif;font-size:80px;font-weight:300;color:#ede8e2;line-height:1}
  .pd-nf-title{font-family:'Cormorant Garamond',serif;font-size:28px;font-weight:300;color:#1a1410}
  .pd-nf-sub{font-size:14px;font-weight:300;color:#a09890;margin-bottom:12px}
  .pd-back-btn{font-size:11px;font-weight:500;letter-spacing:.18em;text-transform:uppercase;text-decoration:none;color:#fdf9f6;background:#1a1410;padding:12px 28px;transition:background .2s}
  .pd-back-btn:hover{background:#c87090}
  @keyframes pd-spin{to{transform:rotate(360deg)}}
  @keyframes pd-shake{0%,100%{transform:translateX(0)}25%{transform:translateX(-5px)}75%{transform:translateX(5px)}}
`;